import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ArrowLeft, Save, FlaskConical, Tag, Clock, IndianRupee, StickyNote } from "lucide-react";
import { updateService, getServiceDataById } from "../../apiCalls/services";
import { fetchCategories, fetchSubcategoriesByCategoryId } from "../../apiCalls/category";

const UpdateService = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const labId     = location.pathname.split("/")[2];
  const serviceId = location.pathname.split("/")[4];

  const { categories }    = useSelector((state) => state.category);
  const { subcategories } = useSelector((state) => state.subcategory);

  const [formValues, setFormValues] = useState({ testName: "", testType: "", duration: "", charges: "", notes: "" });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit,   setIsSubmit]   = useState(false);
  const [isLoading,  setIsLoading]  = useState(false);

  useEffect(() => { fetchCategories(dispatch); }, [dispatch]);

  useEffect(() => {
    getServiceDataById(serviceId).then((data) => {
      if (data) {
        setFormValues((p) => ({ ...p, ...data }));
        if (data.testType?._id || data.testType) {
          fetchSubcategoriesByCategoryId(dispatch, data.testType?._id || data.testType);
        }
      }
    });
  }, [serviceId]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormValues((p) => ({ ...p, [name]: value }));
    if (formErrors[name]) setFormErrors((e) => ({ ...e, [name]: "" }));
    if (name === "testType") fetchSubcategoriesByCategoryId(dispatch, value);
  };

  const validate = (v) => {
    const err = {};
    if (!v.testName) err.testName = "Test name is required";
    if (!v.duration) err.duration = "Duration is required";
    if (!v.charges)  err.charges  = "Charges are required";
    return err;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate(formValues);
    setFormErrors(err);
    setIsSubmit(true);
  };

  const handleClick = () => {
    setIsLoading(true);
    updateService(dispatch, serviceId, formValues, (res, ok) => {
      setIsLoading(false);
      if (ok) { toast.success(res.data.message); navigate(`/profile/${labId}`); }
      else toast.error("Something went wrong. Please try again.");
    });
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) handleClick();
  }, [formErrors]);

  return (
    <div className="svc-form-page">

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
        <button
          className="app-btn app-btn--ghost app-btn--sm"
          onClick={() => navigate(`/profile/${labId}`)}
          type="button"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h4 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "var(--gray-900)" }}>
            Update Service
          </h4>
          <p style={{ fontSize: "0.78rem", color: "var(--gray-400)", margin: 0 }}>
            Edit the details for this diagnostic test
          </p>
        </div>
      </div>

      {/* Form card */}
      <div className="form-card" style={{ maxWidth: 720 }}>
        <div className="form-card-header">
          <h5 className="form-card-title">
            <FlaskConical size={15} /> Service Details
          </h5>
        </div>

        <div className="form-card-body">
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1.25rem" }}>

              {/* Test Type */}
              <div className="field">
                <label className="field-label">
                  <Tag size={12} /> Test Category
                </label>
                <select
                  name="testType"
                  value={formValues.testType?._id || formValues.testType || ""}
                  onChange={handleInput}
                  className="field-select"
                >
                  <option value="">Select category</option>
                  {categories.map((item) => (
                    <option key={item._id} value={item._id}>{item.categoryName}</option>
                  ))}
                </select>
              </div>

              {/* Test Name */}
              <div className="field">
                <label className="field-label">
                  <FlaskConical size={12} /> Test Name <span className="req">*</span>
                </label>
                <select
                  name="testName"
                  value={formValues.testName?._id || formValues.testName || ""}
                  onChange={handleInput}
                  className={`field-select ${formErrors.testName ? "is-error" : ""}`}
                >
                  <option value="">Select test name</option>
                  {subcategories?.map((item) => (
                    <option key={item._id} value={item._id}>{item.subCategoryName}</option>
                  ))}
                </select>
                {formErrors.testName && <span className="field-error">{formErrors.testName}</span>}
              </div>

              {/* Duration */}
              <div className="field">
                <label className="field-label">
                  <Clock size={12} /> Duration (minutes) <span className="req">*</span>
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formValues.duration || ""}
                  onChange={handleInput}
                  className={`field-input ${formErrors.duration ? "is-error" : ""}`}
                  placeholder="e.g. 30"
                  min={1}
                />
                {formErrors.duration && <span className="field-error">{formErrors.duration}</span>}
              </div>

              {/* Charges */}
              <div className="field">
                <label className="field-label">
                  <IndianRupee size={12} /> Service Charges (₹) <span className="req">*</span>
                </label>
                <input
                  type="number"
                  name="charges"
                  value={formValues.charges || ""}
                  onChange={handleInput}
                  className={`field-input ${formErrors.charges ? "is-error" : ""}`}
                  placeholder="e.g. 500"
                  min={0}
                />
                {formErrors.charges && <span className="field-error">{formErrors.charges}</span>}
              </div>

            </div>

            {/* Notes — full width, 20 word max */}
            <div className="field" style={{ marginTop: "0.25rem" }}>
              <label className="field-label">
                <StickyNote size={12} /> Notes
                <span style={{ marginLeft: "auto", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: "0.72rem", color: (() => { const w = (formValues.notes || "").trim().split(/\s+/).filter(Boolean).length; return w >= 20 ? "var(--danger)" : w >= 16 ? "#d97706" : "var(--gray-400)"; })() }}>
                  {(formValues.notes || "").trim().split(/\s+/).filter(Boolean).length} / 10 words
                </span>
              </label>
              <textarea
                name="notes"
                value={formValues.notes || ""}
                  onChange={handleInput}

                // onChange={(e) => {
                //   const words = e.target.value.trim().split(/\s+/).filter(Boolean);
                //   if (words.length <= 20 || e.target.value.endsWith(" ") === false) {
                //     // allow typing freely but cap at 20 words
                //     const capped = words.slice(0, 20).join(" ");
                //     const isDeleting = e.target.value.length < (formValues.notes || "").length;
                //     handleInput({ target: { name: "notes", value: isDeleting ? e.target.value : capped } });
                //   }
                // }}
                rows={2}
                className="field-textarea"
                placeholder="Short note about this service (max 10 words)…"
                style={{ resize: "none" }}
              />
            </div>

            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid var(--gray-100)" }}>
              <button
                type="submit"
                className="app-btn app-btn--primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <><span className="svc-spinner" /> Saving…</>
                ) : (
                  <><Save size={14} /> Save Changes</>
                )}
              </button>
              <button
                type="button"
                className="app-btn app-btn--ghost"
                onClick={() => navigate(`/profile/${labId}`)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateService;
